/**
 * Safe HTML strings. Every interpolated value is escaped unless it is already an {@link Html} (built by `html`).
 * Views render to strings so they can be tested in Node and diffed before touching the DOM.
 */

/** A string of trusted markup. */
export class Html {
  readonly value: string;
  constructor(value: string) {
    this.value = value;
  }
  toString(): string {
    return this.value;
  }
}

const ESCAPES: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

/** Escape text for an element body or a quoted attribute. */
export function escape(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ESCAPES[c] ?? c);
}

type Part = Html | string | number | boolean | null | undefined | readonly Part[];

function render(part: Part): string {
  if (part === null || part === undefined || part === false) return "";
  if (part instanceof Html) return part.value;
  if (Array.isArray(part)) return part.map(render).join("");
  return escape(String(part));
}

/** Tagged template: `html\`<b>${name}</b>\``. Arrays are joined; null, undefined and false render nothing. */
export function html(strings: TemplateStringsArray, ...parts: Part[]): Html {
  let out = strings[0] ?? "";
  for (let i = 0; i < parts.length; i++) out += render(parts[i]) + (strings[i + 1] ?? "");
  return new Html(out);
}

/** Join pieces with a separator, escaping each. */
export function join(parts: readonly Part[], separator: Part): Html {
  return new Html(parts.map(render).join(render(separator)));
}

/** A link, only for http(s) URLs. Anything else renders as plain text. */
export function link(url: string, label?: string): Html {
  let safe = false;
  try {
    const u = new URL(url);
    safe = u.protocol === "https:" || u.protocol === "http:";
  } catch {
    safe = false;
  }
  if (!safe) return html`<span>${label ?? url}</span>`;
  return html`<a href="${url}" target="_blank" rel="noopener noreferrer">${label ?? url}</a>`;
}
